// Play shell (gameplay 2027): the pinned main action ("Weiter" …, marked with
// `js-cta-reveal`) waits until the content has been scrolled to its end, so long
// texts get read first. While content continues below it carries `is-pending`
// (hidden, see _station.scss). Once the end was reached by scrolling it stays
// visible; content that fits the screen shows it right away. Each CTA element is
// tracked on its own, so the next step's CTA starts hidden again.
//
// A station's main action (`js-cta-pin`) is never hidden: until the end was
// reached it carries `is-unpinned` and sits at the end of the content, then it
// stays pinned above the tab bar. Content that fits the screen pins it right away.

const SELECTOR = '.js-cta-reveal, .js-cta-pin';
const PENDING = 'is-pending';
const UNPINNED = 'is-unpinned';
// "at the end" with a little slack (rounding, overscroll)
const END_SLACK = 40;

const revealed = new WeakSet<Element>();
let frame = 0;

// the CTA is fixed, the content scrolls in an ancestor (.singha-body) or the page
function scrollerOf(cta: Element): Element | null {
	for (let el = cta.parentElement; el && el !== document.body; el = el.parentElement) {
		const overflowY = getComputedStyle(el).overflowY;
		if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
			return el;
		}
	}

	const page = document.scrollingElement;
	return page && page.scrollHeight > page.clientHeight + 1 ? page : null;
}

function update(): void {
	frame = 0;

	document.querySelectorAll(SELECTOR).forEach((cta) => {
		const waiting = cta.classList.contains('js-cta-pin') ? UNPINNED : PENDING;

		if (revealed.has(cta)) {
			cta.classList.remove(waiting);
			return;
		}

		const scroller = scrollerOf(cta);

		// an unpinned CTA in the flow takes less room than the pinned one keeps free
		// (_station.scss): the content may fit once it is unpinned — pinning it again
		// would let it cover the end of the content (and flip back and forth)
		if (scroller == null && waiting === UNPINNED && cta.classList.contains(UNPINNED)) {
			return;
		}

		// the slack only once scrolled: on page load (images still loading) a page that is
		// a little too long is not "at the end", or the CTA would stay pinned over its end
		const slack = scroller != null && scroller.scrollTop > 0 ? END_SLACK : 0;
		const atEnd = scroller == null || scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - slack;

		// reached by scrolling: keep it, even when images load further down later
		if (atEnd && scroller != null && scroller.scrollTop > 0) {
			revealed.add(cta);
		}

		cta.classList.toggle(waiting, !atEnd);
	});
}

function schedule(): void {
	if (frame === 0) {
		frame = requestAnimationFrame(update);
	}
}

export function initPlayCtaReveal(): void {
	// capture phase: also sees the inner scroll container and image/iframe loads
	document.addEventListener('scroll', schedule, { capture: true, passive: true });
	document.addEventListener('load', schedule, { capture: true });
	window.addEventListener('resize', schedule);

	// content that shrinks or grows without a scroll (late data, fonts, the browser
	// bar) — otherwise a CTA hidden while the content was still taller would stay
	// hidden on a screen that no longer scrolls
	const resizes = new ResizeObserver(schedule);

	// Blazor renders steps and CTAs in place; check before the first paint of a new CTA
	new MutationObserver(() => {
		if (document.querySelector(SELECTOR)) {
			document.querySelectorAll('.c-play').forEach((content) => resizes.observe(content));
			update();
		}
	}).observe(document.body, { childList: true, subtree: true });
}
